// Microbenchmark de boot — ver §6.2.3 do PLAN.md e docs/specs/tv-compat.md §5.
//
// Roda um punhado de micro-tasks (Math/JSON/DOM/Canvas) e mede o TEMPO
// DECORRIDO (`elapsed` em ms). O tempo é o sinal — quanto mais rápido o
// device, menor o elapsed. `signals/cpu.ts` classifica DIRETO por elapsed
// (limiares em §5.3: ultra <3 · high 3-8 · mid 8-20 · low 20-80 · vlow ≥80).
//
// Por que elapsed e não o "benchScore" antigo (`round(score/elapsed*100)`): o
// score invertido AMPLIFICAVA ruído (poucos ms de variação viravam milhares de
// pontos) e era ilegível ("vlow < 500" implicava, na prática, "elapsed > 2s" —
// limiar que NENHUMA TV real alcança, então nada caía em vlow). Detalhe do bug
// em tv-compat.md §5.1.

/** Roda UMA passada do microbench e retorna o elapsed em ms. Retorna 0 quando
 *  `performance` é indisponível (não dá pra medir). */
function runBenchOnce(): number {
  if (typeof performance === 'undefined') return 0

  const start = performance.now()
  let score = 0
  // Sink defensivo: acumula resultado das operações pra que o JIT não elimine o
  // cálculo como dead code. Sem isso, V8 moderno detecta que o resultado é
  // descartado e pula o trabalho — invalidando o benchmark.
  let sink = 0

  // 1. Math (CPU puro)
  for (let i = 0; i < 10000; i++) {
    sink += Math.sqrt(i) * Math.sin(i)
    score++
  }

  // 2. JSON parse — também acumula em sink. JSON.parse não tem side-effect
  // observável, V8 não garante elisão mas pode rodar mais rápido se vir
  // resultado descartado. Acumular .length força o trabalho real.
  const json = '[' + Array(100).fill('{"a":1,"b":[1,2,3],"c":"x"}').join(',') + ']'
  for (let i = 0; i < 50; i++) {
    sink += (JSON.parse(json) as unknown[]).length
    score++
  }

  // 3. DOM
  if (typeof document !== 'undefined') {
    const el = document.createElement('div')
    for (let i = 0; i < 100; i++) {
      el.appendChild(document.createElement('span'))
    }
    score += 50
  }

  // 4. Canvas
  if (typeof document !== 'undefined') {
    const c = document.createElement('canvas')
    c.width = 100
    c.height = 100
    const ctx = c.getContext('2d')
    if (ctx) {
      for (let i = 0; i < 100; i++) {
        ctx.fillRect(i, i, 10, 10)
      }
      score += 50
    }
  }

  const elapsed = performance.now() - start
  // Lê sink no fim pra impedir V8 de eliminar os loops como dead code (sem
  // leitura observável, optimizer poderia pular o trabalho).
  if (Number.isNaN(sink) || score === 0) return 0
  return elapsed
}

/** Mede o elapsed (ms) do microbench de forma ROBUSTA: descarta a 1ª passada
 *  (o "cold run" é outlier volátil — JIT frio, cache frio; ver tv-compat.md
 *  §5.2, onde cold chegou a 544ms num device cuja mediana era 4.4ms) e retorna
 *  a MEDIANA de 3 passadas medidas. Mediana (não min) pra não superestimar o
 *  device: o throttle de CPU já é um proxy OTIMISTA de silício real de TV, então
 *  não compomos otimismo em cima de otimismo.
 *
 *  Custo one-time (~4 passadas); `signals/cpu.ts` cacheia o resultado. Retorna
 *  0 quando `performance` é indisponível. */
export function measureBenchElapsedMs(): number {
  if (typeof performance === 'undefined') return 0

  // Warmup descartado (cold outlier).
  runBenchOnce()

  const samples: number[] = []
  for (let i = 0; i < 3; i++) {
    const e = runBenchOnce()
    if (e > 0) samples.push(e)
  }
  if (samples.length === 0) return 0

  samples.sort((a, b) => a - b)
  const mid = Math.floor(samples.length / 2)
  return samples.length % 2 === 0 ? (samples[mid - 1]! + samples[mid]!) / 2 : samples[mid]!
}

/** @deprecated Use `measureBenchElapsedMs()` + classificação por elapsed.
 *  Mantido só por retrocompat da API pública (`@app-iptv/device`). Deriva o
 *  "score" legado (`round(1015000/elapsed)`) da mediana de elapsed pra que
 *  qualquer telemetria histórica que grave `benchScore` continue com a mesma
 *  escala — mas o score NÃO é mais usado pra decidir tier. */
export function quickBenchmark(): number {
  const elapsed = measureBenchElapsedMs()
  if (elapsed <= 0) return 0
  // 1015000 = score fixo antigo (10150 "pontos") × 100, preservado pra a escala
  // do benchScore legado bater com a histórica.
  return Math.round(1_015_000 / elapsed)
}
